import React, { useEffect } from "react";
import styled from "styled-components";
import {
  Headphones,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Calendar,
  CalendarDays,
  CalendarRange,
  Mail,
  Eye,
} from "lucide-react";
import moment from "moment";
import { useTicketsStore } from "./use-tickets-store";
import {
  TICKET_STATUS,
  TICKET_PRIORITY,
  getStatusText,
  getPriorityText,
  getSectionLabel,
} from "./tickets-types";
import type { ITicket, TicketStatus, TicketPriority } from "./tickets-types";
import { ViewTicketModal } from "./view-ticket-modal";
import { DeleteTicketModal } from "./delete-ticket-modal";

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const HeaderIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #ede7f6;
  flex-shrink: 0;
`;

const PageTitle = styled.h1`
  font-size: 21px;
  font-weight: 700;
  color: #202020;
  margin: 0;
`;

const TotalBadge = styled.span`
  font-size: 12px;
  opacity: 0.75;
`;

// ─── Stats Cards ──────────────────────────────────────────────────────────────

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 12px;
`;

const SecondaryStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: white;
  border: 1px solid #eee;
  border-radius: 10px;
`;

const StatIconBox = styled.div<{ $bg: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(p) => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #202020;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #999;
`;

// ─── Filters ──────────────────────────────────────────────────────────────────

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  max-width: 320px;
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
  display: flex;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: #9b59b6;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  background: white;
  cursor: pointer;
  &:focus {
    border-color: #9b59b6;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableWrap = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
`;

const EmptyTd = styled.td`
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

const SubjectCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9b59b6;
  flex-shrink: 0;
`;

const statusColors: Record<string, { bg: string; color: string }> = {
  [TICKET_STATUS.OPEN]: { bg: "#fff3e0", color: "#ff9800" },
  [TICKET_STATUS.WAITING]: { bg: "#e3f2fd", color: "#1565c0" },
  [TICKET_STATUS.RESOLVED]: { bg: "#e8f5e9", color: "#4caf50" },
  [TICKET_STATUS.CLOSED]: { bg: "#ffebee", color: "#f44336" },
};

const priorityColors: Record<string, { bg: string; color: string }> = {
  [TICKET_PRIORITY.HIGH]: { bg: "#ffebee", color: "#f44336" },
  [TICKET_PRIORITY.NORMAL]: { bg: "#fff3e0", color: "#ff9800" },
  [TICKET_PRIORITY.LOW]: { bg: "#e8f5e9", color: "#4caf50" },
};

const Badge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  white-space: nowrap;
`;

const DeletedRow = styled.tr`
  opacity: 0.5;
  background: #fafafa;
`;

const OpenButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid #9b59b6;
  border-radius: 6px;
  background: transparent;
  color: #9b59b6;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #9b59b6;
    color: white;
  }
`;

// ─── Pagination ───────────────────────────────────────────────────────────────

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 5px 10px;
  border: 1px solid ${({ $active }) => ($active ? "#9b59b6" : "#e0e0e0")};
  border-radius: 4px;
  background: ${({ $active }) => ($active ? "#9b59b6" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#333")};
  cursor: pointer;
  font-size: 13px;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function renderStatusBadge(status: TicketStatus) {
  const c = statusColors[status] || { bg: "#f5f5f5", color: "#999" };
  return (
    <Badge $bg={c.bg} $color={c.color}>
      {getStatusText(status)}
    </Badge>
  );
}

function renderPriorityBadge(priority: TicketPriority) {
  const c = priorityColors[priority] || { bg: "#f5f5f5", color: "#999" };
  return (
    <Badge $bg={c.bg} $color={c.color}>
      {getPriorityText(priority)}
    </Badge>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TicketsPage: React.FC = () => {
  const {
    list,
    total,
    stats,
    page,
    limit,
    statusFilter,
    priorityFilter,
    query,
    showDeleted,
    loading,
    modalView,
    modalDelete,
    fetch,
    fetchStats,
    setPage,
    setLimit,
    setStatusFilter,
    setPriorityFilter,
    setQuery,
    setShowDeleted,
    openViewModal,
    openDeleteModal,
  } = useTicketsStore();

  useEffect(() => {
    fetch();
    fetchStats();
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <PageContainer>
      <PageHeader>
        <HeaderIconWrap>
          <Headphones size={20} color="#9b59b6" />
        </HeaderIconWrap>
        <PageTitle>
          Обращения клиентов
          <TotalBadge> ({total})</TotalBadge>
        </PageTitle>
      </PageHeader>

      {/* Основная статистика */}
      {stats && (
        <StatsGrid>
          <StatCard>
            <StatIconBox $bg="#fff3e0">
              <AlertCircle size={20} color="#ff9800" />
            </StatIconBox>
            <div>
              <StatValue>{stats.open}</StatValue>
              <StatLabel>Открытые</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIconBox $bg="#e3f2fd">
              <Clock size={20} color="#1565c0" />
            </StatIconBox>
            <div>
              <StatValue>{stats.waiting}</StatValue>
              <StatLabel>Ожидание</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIconBox $bg="#e8f5e9">
              <CheckCircle size={20} color="#4caf50" />
            </StatIconBox>
            <div>
              <StatValue>{stats.resolved}</StatValue>
              <StatLabel>Решённые</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIconBox $bg="#ffebee">
              <XCircle size={20} color="#f44336" />
            </StatIconBox>
            <div>
              <StatValue>{stats.closed}</StatValue>
              <StatLabel>Закрытые</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIconBox $bg="#ffebee">
              <AlertTriangle size={20} color="#f44336" />
            </StatIconBox>
            <div>
              <StatValue>{stats.high_priority}</StatValue>
              <StatLabel>Высокий приоритет</StatLabel>
            </div>
          </StatCard>
        </StatsGrid>
      )}

      {/* Вторичная статистика */}
      {stats && (
        <SecondaryStatsGrid>
          <StatCard>
            <StatIconBox $bg="#ede7f6">
              <Calendar size={20} color="#9b59b6" />
            </StatIconBox>
            <div>
              <StatValue>{stats.today}</StatValue>
              <StatLabel>Сегодня</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIconBox $bg="#ede7f6">
              <CalendarDays size={20} color="#9b59b6" />
            </StatIconBox>
            <div>
              <StatValue>{stats.this_week}</StatValue>
              <StatLabel>За неделю</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIconBox $bg="#ede7f6">
              <CalendarRange size={20} color="#9b59b6" />
            </StatIconBox>
            <div>
              <StatValue>{stats.this_month}</StatValue>
              <StatLabel>За месяц</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIconBox $bg="#ede7f6">
              <Mail size={20} color="#9b59b6" />
            </StatIconBox>
            <div>
              <StatValue>{stats.unread_count}</StatValue>
              <StatLabel>Непрочитанные</StatLabel>
            </div>
          </StatCard>
        </SecondaryStatsGrid>
      )}

      {/* Фильтры */}
      <FiltersRow>
        <SearchWrap>
          <SearchIconWrap>
            <Search size={16} />
          </SearchIconWrap>
          <SearchInput
            type="text"
            placeholder="Поиск по теме, автору..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchWrap>

        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Все статусы</option>
          <option value={TICKET_STATUS.OPEN}>Открытые</option>
          <option value={TICKET_STATUS.WAITING}>Ожидание</option>
          <option value={TICKET_STATUS.RESOLVED}>Решённые</option>
          <option value={TICKET_STATUS.CLOSED}>Закрытые</option>
        </FilterSelect>

        <FilterSelect
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          <option value="">Все приоритеты</option>
          <option value={TICKET_PRIORITY.HIGH}>Высокий</option>
          <option value={TICKET_PRIORITY.NORMAL}>Обычный</option>
          <option value={TICKET_PRIORITY.LOW}>Низкий</option>
        </FilterSelect>

        <CheckboxLabel>
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowDeleted(e.target.checked)}
          />
          Показать удалённые
        </CheckboxLabel>

        <FilterSelect
          value={String(limit)}
          onChange={(e) => setLimit(Number(e.target.value))}
          style={{ marginLeft: "auto" }}
        >
          <option value="10">10 на стр.</option>
          <option value="25">25 на стр.</option>
          <option value="50">50 на стр.</option>
          <option value="100">100 на стр.</option>
        </FilterSelect>
      </FiltersRow>

      {/* Таблица */}
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Тема</Th>
              <Th>Категория</Th>
              <Th>Автор</Th>
              <Th>Статус</Th>
              <Th>Приоритет</Th>
              <Th>Сообщения</Th>
              <Th>Активность</Th>
              <Th>Дата</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <EmptyTd colSpan={9}>Загрузка...</EmptyTd>
              </tr>
            )}
            {!loading && list.length === 0 && (
              <tr>
                <EmptyTd colSpan={9}>Нет обращений</EmptyTd>
              </tr>
            )}
            {!loading &&
              list.map((ticket: ITicket) => {
                const Row = ticket.deleted_at ? DeletedRow : "tr";
                return (
                  <Row key={ticket.uuid}>
                    <Td>
                      <SubjectCell>
                        {!ticket.is_read_by_support && <UnreadDot />}
                        <span style={{ fontWeight: 500 }}>
                          {ticket.title}
                        </span>
                      </SubjectCell>
                      {ticket.deleted_at && (
                        <div style={{ fontSize: 10, color: "#f44336" }}>
                          Удалён
                        </div>
                      )}
                    </Td>
                    <Td>
                      <span style={{ color: "#666", fontSize: 12 }}>
                        {getSectionLabel(ticket.section)}
                      </span>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 500 }}>
                        {ticket.author_login || "—"}
                      </div>
                      {ticket.author_email && (
                        <div style={{ fontSize: 11, color: "#999" }}>
                          {ticket.author_email}
                        </div>
                      )}
                    </Td>
                    <Td>{renderStatusBadge(ticket.status as TicketStatus)}</Td>
                    <Td>{renderPriorityBadge(ticket.priority as TicketPriority)}</Td>
                    <Td>
                      <span style={{ fontWeight: 500 }}>
                        {ticket.messages_count ?? 0}
                      </span>
                    </Td>
                    <Td style={{ fontSize: 12, color: "#666" }}>
                      {moment(ticket.last_activity_at).fromNow()}
                    </Td>
                    <Td style={{ fontSize: 12, color: "#999" }}>
                      {moment(ticket.created_at).format("DD.MM.YYYY HH:mm")}
                    </Td>
                    <Td>
                      <OpenButton onClick={() => openViewModal(ticket)}>
                        <Eye size={14} />
                        Открыть
                      </OpenButton>
                    </Td>
                  </Row>
                );
              })}
          </tbody>
        </Table>
      </TableWrap>

      {/* Пагинация */}
      {totalPages > 1 && (
        <PaginationRow>
          <PageButton
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            &larr; Назад
          </PageButton>
          <span style={{ fontSize: 13, color: "#888" }}>
            {page} / {totalPages}
          </span>
          <PageButton
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Вперёд &rarr;
          </PageButton>
        </PaginationRow>
      )}

      {/* Модалки */}
      {modalView && <ViewTicketModal />}
      {modalDelete && <DeleteTicketModal />}
    </PageContainer>
  );
};
